/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";

const TAG: string = "serviceCache";

/**
 * A domain-lib service that owns an LRU cache and can drop it.
 *
 * Structural rather than a union of the concrete service classes: the services
 * keep their `LruCache` handle private, so the static `clearCache()` they expose
 * is the only seam callers have — and the only thing this module needs.
 */
export interface ICacheOwningService {
  /** Drops every entry the service has cached, leaving it usable afterwards. */
  clearCache: () => void;
}

/**
 * Drops the LRU cache entries belonging to `service`.
 *
 * The one place the app invalidates a domain-lib cache, so every mutation that
 * needs to do so reads the same way and lands in the log with the caller
 * attached.
 *
 * Never throws: `LruCache.deleteServiceCache` rejects a service it has no entry
 * for, which happens whenever the service has not been constructed yet in this
 * session. That is not a failure worth propagating — an empty cache is exactly
 * the state the caller wanted — and a mutation must not fail because its cache
 * invalidation did.
 *
 * @param service - The service whose cached entries are being dropped, e.g.
 * `ActionsService`. Passed in rather than hard-coded so a second cache can be
 * invalidated the same way without a second helper.
 * @param callerTag - Log tag of the caller, so a line names the mutation that
 * triggered the invalidation.
 */
export const clearServiceCache = (
  service: ICacheOwningService,
  callerTag: string
): void => {
  try {
    service.clearCache();
  } catch (error: unknown) {
    Log.w(TAG, `${callerTag} — clearServiceCache() failed`, error);
  }
};
